import { z } from "zod";
import { ExecuteSimulationRequestSchema } from "./simulations.js";

// ── Janela de calibração discreta + nível de rodada (preview/detalhada) ────────────
// A janela de calibração deixa de ser input livre: 15/30/60/90 dias (padrão 90). Só MTBF/MTTR
// são deriváveis dela; ritmo/ciclo/qualidade seguem simProfile/premissa. `RunLevel` distingue o
// preview leve (efêmero, imediato) da rodada detalhada (DES real, persistida).
export const CALIBRATION_DAYS = [15, 30, 60, 90] as const;
export const CalibrationDaysSchema = z
  .union([z.literal(15), z.literal(30), z.literal(60), z.literal(90)])
  .default(90);
export type CalibrationDays = (typeof CALIBRATION_DAYS)[number];

export const RunLevelSchema = z.enum(["preview", "detailed"]);
export type RunLevel = z.infer<typeof RunLevelSchema>;

// ── Execução de cenário estrutural com período/OP + gate de compat. ────────────
// O período define o horizonte (nunca digitado direto no fluxo novo): intervalo livre
// ou janela de uma Ordem de Produção (planejada ou executada/backtest). `calibrationDays`
// (só "avançado" na UI) parametriza a janela do cold-start no worker.
export const SimulationPeriodSchema = z
  .object({
    mode: z.enum(["FREE", "OP_PLANNED", "OP_EXECUTED"]),
    periodStart: z.string().datetime().optional(),
    periodEnd: z.string().datetime().optional(),
    sourceProductionOrderId: z.string().uuid().optional(),
    calibrationDays: CalibrationDaysSchema,
  })
  // superRefine: cada issue ancora no CAMPO faltante/errado, não num âncora fixo — o
  // worker classifica "obrigatório ausente" olhando o valor no path do issue; um path que aponta
  // campo presente quando o ausente é outro (ex.: só periodStart faltando com path fixo em
  // periodEnd) faria o descarte morrer mudo de novo.
  .superRefine((v, ctx) => {
    if (v.mode === "FREE") {
      if (v.periodStart == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["periodStart"],
          message: "Intervalo livre exige início.",
        });
      }
      if (v.periodEnd == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["periodEnd"],
          message: "Intervalo livre exige fim.",
        });
      }
      if (
        v.periodStart != null &&
        v.periodEnd != null &&
        new Date(v.periodEnd) <= new Date(v.periodStart)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["periodEnd"],
          message: "Fim deve ser posterior ao início.",
        });
      }
      return;
    }
    if (v.sourceProductionOrderId == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sourceProductionOrderId"],
        message: "Selecione a Ordem de Produção do período.",
      });
    }
  });
export type SimulationPeriod = z.infer<typeof SimulationPeriodSchema>;

export const ExecuteScenarioRequestSchema = z.object({
  period: SimulationPeriodSchema,
  isBaseline: z.boolean().default(false), // true = rodada da linha/config sem alterações
});
export type ExecuteScenarioRequest = z.infer<typeof ExecuteScenarioRequestSchema>;

// Corpo do preview leve síncrono (POST /scenarios/:id/preview). Só o período
// (reusa calibrationDays); efêmero (não persiste). Resposta = PreviewResponseSchema.
export const ScenarioPreviewRequestSchema = z.object({ period: SimulationPeriodSchema });
export type ScenarioPreviewRequest = z.infer<typeof ScenarioPreviewRequestSchema>;

// Corpo aceito por POST /scenarios/:id/executions. Transitório: aceita o fluxo novo
// (cenário estrutural, period/isBaseline) e o legado (productId/horizonHours do builder antigo,
// removido). Serviço discrimina por presença de `period`.
export const ScenarioExecutionBodySchema = z.union([
  ExecuteScenarioRequestSchema,
  ExecuteSimulationRequestSchema,
]);
export type ScenarioExecutionBody = z.infer<typeof ScenarioExecutionBodySchema>;

// 409 pré-enqueue: máquinas `present` com CompatibilityMatrix.enabled=false vs. productId.
export const IncompatibleRunErrorSchema = z.object({
  code: z.literal("SCENARIO_PRODUCT_INCOMPATIBLE"),
  conflicts: z
    .array(
      z.object({
        machineId: z.string().uuid(),
        machineCode: z.string(),
        productId: z.string().uuid(),
      })
    )
    .min(1),
});
export type IncompatibleRunError = z.infer<typeof IncompatibleRunErrorSchema>;
